// The ordering clock. Pure, with an injectable wall clock, and importing
// nothing — server-side compaction is a designed future host for this module
// and it must run outside a browser.
//
// Two clocks do two jobs in this design and conflating them is where systems
// of this shape usually go wrong. THIS one orders events. It is never
// used to decide coverage: a device with a slow clock would hide its own
// events behind a cursor built from its own stamps. Coverage uses Appwrite's
// server-assigned time, and lives in sync/.

/** A hybrid logical clock reading, serialized so that `a < b` is the order. */
export type Stamp = string;

/** A per-install id, minted once per browser. See device.ts. */
export type DeviceId = string;

/** An event's id, which is also its Appwrite row id. */
export type EventId = string;

// Base-36, zero-padded to a fixed width so that lexicographic order is
// numeric order. 36^10 ms is roughly the year 116000, and 36^4 events inside
// a single millisecond is 1.6 million — neither bound is reachable, and both
// are asserted by clock.test.ts's fixed-width test rather than assumed.
const WALL_WIDTH = 10;
const CTR_WIDTH = 4;

const encode = (wall: number, ctr: number): Stamp =>
	`${wall.toString(36).padStart(WALL_WIDTH, "0")}.${ctr.toString(36).padStart(CTR_WIDTH, "0")}`;

interface Reading {
	wall: number;
	ctr: number;
}

// A stamp is exactly ten base-36 digits, a dot, and four more.
//
// Validated by pattern rather than by parseInt, because parseInt(s, 36) stops
// at the first invalid character and returns what it parsed so far — so
// "0000000000.000!" would decode to a plausible reading instead of being
// refused, and observe() would take that value into this device's clock.
const STAMP = /^[0-9a-z]{10}\.[0-9a-z]{4}$/;

/**
 * Parses a stamp back into its parts, or null if it is not one.
 *
 * Nullable rather than throwing because the only callers that see an
 * unparseable stamp are reading a row somebody else's build wrote, and such a
 * row must be skipped, not crash the fold.
 */
function decode(s: Stamp): Reading | null {
	if (!STAMP.test(s)) return null;
	// Both segments are base-36 digits by the pattern above, so neither parse
	// can be partial and neither can be NaN.
	return { wall: parseInt(s.slice(0, WALL_WIDTH), 36), ctr: parseInt(s.slice(WALL_WIDTH + 1), 36) };
}

/** The wall reading a stamp carries. -1 for a stamp that is not one. */
export const stampWall = (s: Stamp): number => decode(s)?.wall ?? -1;

/**
 * Total order over well-formed stamps. Fixed-width encoding means this is a
 * string compare; the function exists anyway so that `fold` reads as ordering
 * by a stamp rather than by an incidental string property.
 *
 * It gives malformed input no meaningful position, and does not need to:
 * `stampWall` returns -1 for anything `decode` refuses, and the reducer drops
 * an event whose stamp has no wall reading, so where such an event landed in
 * the sort never reaches the fold. Stated explicitly because the comment here
 * used to claim malformed stamps sort FIRST, which is backwards — a real
 * stamp begins with "0" for the next hundred thousand years, so most
 * malformed strings sort after every one of them.
 */
export const compareStamps = (a: Stamp, b: Stamp): number => (a < b ? -1 : a > b ? 1 : 0);

export interface Clock {
	/** The next stamp. Strictly greater than every stamp this clock has minted or observed. */
	next(): Stamp;
	/** Merge in a stamp from elsewhere, so this device never mints one that sorts before it. */
	observe(s: Stamp): void;
}

/**
 * A hybrid logical clock.
 *
 * `wall` tracks real time so that stamps stay roughly meaningful and sortable
 * against another device's; `ctr` breaks ties within a millisecond; `observe`
 * is what makes it *logical* rather than merely a local counter — a device
 * whose clock is an hour slow stops minting stamps that sort before everything
 * it has already seen.
 *
 * The device id is NOT part of the stamp. It is a separate field on the event
 * and a separate term in `order()`, which keeps the stamp comparable
 * across devices instead of partitioned by them.
 */
export function makeClock(now: () => number = Date.now): Clock {
	let wall = 0;
	let ctr = 0;
	return {
		next() {
			const t = now();
			if (t > wall) {
				wall = t;
				ctr = 0;
			} else {
				ctr++;
			}
			return encode(wall, ctr);
		},
		observe(s) {
			const r = decode(s);
			if (!r) return;
			if (r.wall > wall) {
				wall = r.wall;
				ctr = r.ctr;
			} else if (r.wall === wall && r.ctr > ctr) {
				ctr = r.ctr;
			}
		},
	};
}
